/**
 * Dynamic values (requirements, Feature 3): date rules and name tokens that are
 * resolved at the moment a template is applied.
 *
 * A date cell can hold a rule instead of a date:
 *   { base: 'apply', offset: 3, unit: 'days' | 'working_days' }
 *   Today / Tomorrow / Yesterday are offsets 0 / 1 / -1 in days. Working days are
 *   Monday–Friday; from a weekend, +1 working day is Monday. No holidays.
 * Stored on the cell as `dynamic`, beside the usual { id, type, value, text }
 * with value null — see columnValues.js.
 *
 * A timeline cell can hold a rule for each end:
 *   { start: { base: 'apply', offset, unit },
 *     end:   { base: 'start' | 'apply', offset, unit } }
 * "Today + 0 days → Start + 4 working days" is a five-working-day span
 * starting the day the template is applied ("Today" in the UI is always the
 * apply date). An end that would fall before the start is pulled up to the
 * start. Next week / Next month are Today → Today + 7 / + 30 days, exact
 * counts, not calendar weeks or months.
 *
 * A name can hold the `{date}` token, printed as "Oct 12, 2026" at apply.
 *
 * Everything resolves once per apply from one base date: today in the applier's
 * timezone (applyBaseDate). All date arithmetic is in UTC on 'YYYY-MM-DD'
 * strings, so DST never shifts a day.
 *
 * Mirrored in spt/src/helpers/dynamic-values.ts, fixture for fixture.
 */
const DAY_MS = 86400000;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const DATE_UNITS = Object.freeze(['days', 'working_days']);
export const MAX_OFFSET = 365;
export const DATE_TOKEN = '{date}';
export const NAME_MAX_CHARS = 255;
export const UNTITLED = 'Untitled';

export const DYNAMIC_PRESETS = Object.freeze([
  { key: 'today', label: 'Today', rule: { base: 'apply', offset: 0, unit: 'days' } },
  { key: 'tomorrow', label: 'Tomorrow', rule: { base: 'apply', offset: 1, unit: 'days' } },
  { key: 'yesterday', label: 'Yesterday', rule: { base: 'apply', offset: -1, unit: 'days' } },
]);

const pad = (n) => String(n).padStart(2, '0');

function parseIso(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
  if (!m) return null;
  const ms = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isFinite(ms) ? ms : null;
}

function toIso(ms) {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

export function isDynamicRule(rule) {
  return Boolean(
    rule &&
      typeof rule === 'object' &&
      rule.base === 'apply' &&
      Number.isInteger(rule.offset) &&
      Math.abs(rule.offset) <= MAX_OFFSET &&
      DATE_UNITS.includes(rule.unit),
  );
}

export function addDays(iso, n) {
  const ms = parseIso(iso);
  return ms === null ? null : toIso(ms + n * DAY_MS);
}

export function addWorkingDays(iso, n) {
  let ms = parseIso(iso);
  if (ms === null) return null;
  const step = n < 0 ? -1 : 1;
  let left = Math.abs(n);
  while (left > 0) {
    ms += step * DAY_MS;
    const dow = new Date(ms).getUTCDay();
    if (dow !== 0 && dow !== 6) left -= 1;
  }
  return toIso(ms);
}

/** The date a rule lands on for a given base date ('YYYY-MM-DD'), or null for a bad rule or date. */
export function resolveDate(rule, baseDate) {
  if (!isDynamicRule(rule)) return null;
  return rule.unit === 'working_days' ? addWorkingDays(baseDate, rule.offset) : addDays(baseDate, rule.offset);
}

export const TIMELINE_END_BASES = Object.freeze(['start', 'apply']);

/** The rule a new dynamic timeline starts from: five working days from the apply date. */
export const DEFAULT_TIMELINE_RULE = Object.freeze({
  start: Object.freeze({ base: 'apply', offset: 0, unit: 'days' }),
  end: Object.freeze({ base: 'start', offset: 4, unit: 'working_days' }),
});

const span = (days) =>
  Object.freeze({
    start: Object.freeze({ base: 'apply', offset: 0, unit: 'days' }),
    end: Object.freeze({ base: 'apply', offset: days, unit: 'days' }),
  });

export const TIMELINE_PRESETS = Object.freeze([
  { key: 'next_week', label: 'Next week', rule: span(7) },
  { key: 'next_month', label: 'Next month', rule: span(30) },
]);

/**
 * The preset a timeline rule is, or null. Matched on what it resolves to:
 * a start of Today and an end N days on, whether the end counts from Start or
 * from Today (the same day when the start is Today).
 */
export function timelinePreset(rule) {
  if (!isTimelineRule(rule) || rule.start.offset !== 0) return null;
  const { end } = rule;
  return TIMELINE_PRESETS.find((p) => end.unit === 'days' && end.offset === p.rule.end.offset) || null;
}

export function isTimelineRule(rule) {
  const end = rule && rule.end;
  return Boolean(
    rule &&
      typeof rule === 'object' &&
      isDynamicRule(rule.start) &&
      end &&
      typeof end === 'object' &&
      TIMELINE_END_BASES.includes(end.base) &&
      Number.isInteger(end.offset) &&
      Math.abs(end.offset) <= MAX_OFFSET &&
      DATE_UNITS.includes(end.unit),
  );
}

/** { from, to } ('YYYY-MM-DD') for a timeline rule and a base date, or null for a bad rule or date. */
export function resolveTimeline(rule, baseDate) {
  if (!isTimelineRule(rule)) return null;
  const from = resolveDate(rule.start, baseDate);
  if (!from) return null;
  let to = resolveDate({ ...rule.end, base: 'apply' }, rule.end.base === 'start' ? from : baseDate);
  if (!to) return null;
  if (to < from) to = from; // ISO dates compare as strings
  return { from, to };
}

const offsetText = (r) => {
  const n = Math.abs(r.offset);
  const unit = r.unit === 'working_days' ? (n === 1 ? 'working day' : 'working days') : n === 1 ? 'day' : 'days';
  return `${r.offset < 0 ? '−' : '+'}${n} ${unit}`;
};

/** "Next week", "Today → Start +4 working days", "Today +2 days → Today +30 days". */
export function describeTimeline(rule) {
  if (!isTimelineRule(rule)) return '';
  const preset = timelinePreset(rule);
  if (preset) return preset.label;
  const part = (label, r) => (r.offset === 0 ? label : `${label} ${offsetText(r)}`);
  return `${part('Today', rule.start)} → ${part(rule.end.base === 'start' ? 'Start' : 'Today', rule.end)}`;
}

/** "Today", "Tomorrow", "Yesterday", "+3 working days", "−2 days". */
export function describeRule(rule) {
  if (!isDynamicRule(rule)) return '';
  const preset = DYNAMIC_PRESETS.find((p) => p.rule.offset === rule.offset && p.rule.unit === rule.unit);
  if (preset) return preset.label;
  const n = Math.abs(rule.offset);
  const unit = rule.unit === 'working_days' ? (n === 1 ? 'working day' : 'working days') : n === 1 ? 'day' : 'days';
  return `${rule.offset < 0 ? '−' : '+'}${n} ${unit}`;
}

/** 'YYYY-MM-DD' → 'Oct 12, 2026'; anything else is returned as given. */
export function formatDate(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
  if (!m) return String(iso || '');
  const month = MONTHS[Number(m[2]) - 1];
  return month ? `${month} ${Number(m[3])}, ${Number(m[1])}` : String(iso);
}

/** Today as 'YYYY-MM-DD' in a timezone; an unknown zone falls back to UTC. */
export function applyBaseDate(timeZone, now = new Date()) {
  let parts;
  try {
    parts = new Intl.DateTimeFormat('en-US', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
  } catch (e) {
    parts = new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
  }
  const get = (type) => (parts.find((p) => p.type === type) || {}).value;
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/**
 * Name with `{date}` filled in. Other braces stay as typed; an empty result is
 * "Untitled"; the result is cut at 255 characters. Without a date the token is
 * left as typed (previews before apply pass one).
 */
export function resolveName(name, ctx) {
  let out = String(name == null ? '' : name);
  if (ctx && ctx.date) out = out.split(DATE_TOKEN).join(formatDate(ctx.date));
  out = out.trim();
  if (!out) out = UNTITLED;
  return out.slice(0, NAME_MAX_CHARS);
}

/**
 * A stored cell with `dynamic` → the same cell with fixed dates (a date rule →
 * { date }, a timeline rule → { from, to }); others untouched; an
 * unresolvable rule → null.
 */
export function resolveColumnValue(cv, baseDate) {
  if (!cv || !cv.dynamic) return cv;
  const { dynamic, ...rest } = cv;
  if (dynamic.start) {
    const t = resolveTimeline(dynamic, baseDate);
    return t ? { ...rest, value: JSON.stringify(t), text: `${t.from} - ${t.to}` } : null;
  }
  const date = resolveDate(dynamic, baseDate);
  if (!date) return null;
  return { ...rest, value: JSON.stringify({ date }), text: date };
}

/**
 * The pre-pass both apply paths run once: every row's name and date rules
 * resolved against one base date, so the loops only ever see fixed values.
 */
export function resolveSubitemsForApply(subitems, baseDate) {
  return (subitems || []).map((s) => ({
    ...s,
    name: resolveName(s.name, { date: baseDate }),
    column_values: (s.column_values || []).map((cv) => resolveColumnValue(cv, baseDate)).filter(Boolean),
  }));
}
